import { Box, Button } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { get } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { FieldValues, FormProvider, UseFormReturn, useForm, useFormContext } from 'react-hook-form';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import {
  ErrorMessage,
  FormActionButtons,
  FormField,
  FormWrapper,
  Label,
} from '../../../components/Form/FormComponents';
import { FormFieldFactory } from '../../../components/Form/FormFieldFactory';
import { RendererType } from '../../../data-models/field-definition';
import { MaggieFeatureFlags } from '../../../util/feature-flags';
import { IForm, IFormField } from '../../../view-models/form.view-model';

const modalStyle = {
  display: 'grid',
  gridGap: '1rem',
  width: '50vw',
  padding: '0 2.5rem 2rem',
};

export type DynamicFormTransformer<T extends FieldValues> = (
  form: IForm<T>,
  methods: UseFormReturn<T>,
  featureFlags: MaggieFeatureFlags
) => IForm<T>;

export interface IConfigModalProps<T extends FieldValues> {
  form: IForm<T>;
  open: boolean;
  onClose: () => void;
  onSubmit: (updatedData: T) => void;
  title: string;
  dynamicFormTransformer?: DynamicFormTransformer<T>;
}
export function ConfigModal<T extends FieldValues>(props: IConfigModalProps<T>) {
  const { form, onClose, open, onSubmit, title } = props;
  const formMethods = useForm({
    values: getInitialValues(form),
  });

  return (
    <BasicDialog
      open={open}
      onClose={onClose}
      title={title}
      aria-labelledby='modal-title'
      aria-describedby='modal-description'
    >
      <Box sx={modalStyle}>
        <FormProvider {...formMethods}>
          <ConfigForm
            form={form}
            onSubmit={onSubmit}
            onCancel={onClose}
            dynamicFormTransformer={props.dynamicFormTransformer}
          />
        </FormProvider>
      </Box>
    </BasicDialog>
  );
}

export interface IConfigFormProps<T extends FieldValues> {
  form: IForm<T>;
  onSubmit: (updatedData: T) => void;
  onCancel: () => void;
  dynamicFormTransformer?: DynamicFormTransformer<T>;
}
/**
 * The schema-driven field grid of a config form: applies the dynamic transformer
 * and renders each field. Must be rendered inside a FormProvider. Extracted from
 * ConfigForm so steppers can compose the fields with their own action buttons.
 */
export function ConfigFormFields<T extends FieldValues>(props: {
  form: IForm<T>;
  dynamicFormTransformer?: DynamicFormTransformer<T>;
}) {
  const flags = useFlags<MaggieFeatureFlags>();
  const { form: staticForm, dynamicFormTransformer } = props;
  const formMethods = useFormContext();

  const [form, setForm] = useState(() => staticForm);
  useEffect(() => {
    if (!dynamicFormTransformer) {
      return;
    }
    setForm(dynamicFormTransformer(staticForm, formMethods as UseFormReturn<T>, flags));
  }, [dynamicFormTransformer, flags, formMethods, staticForm]);

  const formElems = form.fields.toArray((item) => {
    const { label, key, description, required } = item;
    const initialValue = getInitialValue(form, item);

    return (
      <FormField key={key ?? label}>
        <Label required={required} description={description}>
          {label}
        </Label>
        <FormFieldFactory formField={item} initialValue={initialValue} />
      </FormField>
    );
  });

  return <FormWrapper>{formElems}</FormWrapper>;
}

export function ConfigForm<T extends FieldValues>(props: IConfigFormProps<T>) {
  const { form: staticForm, onCancel, onSubmit, dynamicFormTransformer } = props;
  const formMethods = useFormContext();
  const { errors } = formMethods.formState;
  const errorCount = Object.keys(errors).length;

  function _onSubmit(data: FieldValues) {
    onSubmit({
      ...staticForm.data,
      ...data,
    } as T);
  }

  return (
    <form onSubmit={formMethods.handleSubmit(_onSubmit)}>
      <ConfigFormFields form={staticForm} dynamicFormTransformer={dynamicFormTransformer} />
      <FormActionButtons>
        <Button type={'submit'} color={'secondary'} variant={'contained'}>
          Save
        </Button>
        <Button onClick={onCancel} color={'secondary'} variant={'outlined'}>
          Cancel
        </Button>
      </FormActionButtons>
      {errorCount > 0 && <ErrorMessage>{errorCount} error(s) in this form, please review.</ErrorMessage>}
    </form>
  );
}

export function useConfigModal() {
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpen = useCallback((isEdit: boolean) => {
    setIsEdit(isEdit);
    setOpen(true);
  }, []);

  return {
    open,
    handleClose,
    handleOpen,
    isEdit,
  };
}

export function getInitialValues(form: IForm<FieldValues>) {
  return form.fields.reduce(
    (res, item) => {
      res[item.key] = getInitialValue(form, item);
      return res;
    },
    {} as Record<string, unknown>
  );
}

export function getInitialValue(form: IForm<FieldValues>, item: IFormField<unknown>) {
  const value = get(form.data, item.key);
  if (item.renderer === RendererType.singleSelect && value === '') {
    return null;
  }
  return value;
}
